// A series of helper functions used for parsing and cleaning SCOUT job data into usable data for the CR app

interface LocationData {
    radiusMiles: number;
    point: {
        x: number;
        y: number;
    };
}

// As long as SCOUT is incorrectly appending lat/long data as job params, we'll need
// to rely on using the lat/long data from the RADII_MILES param
export const getDataFromRadiusString = (dataStr: string | null): LocationData | null => {
    if (!dataStr) {
        return null;
    }

    const parts = dataStr.split(",");

    if (parts.length < 3) {
        return null;
    }

    const latitude = Number(parts[0]);
    const longitude = Number(parts[1]);
    const radiusMiles = Number(parts[2].split(" ")[0]); // Extract the miles value before any text

    return {
        radiusMiles,
        point: {
            x: latitude,
            y: longitude,
        },
    };
};

const cleanRing = (ring: number[][]): number[][] => {
    if (ring.length < 4) {
        throw new Error("Polygon ring must have at least 4 points");
    }

    const firstPoint = ring[0];
    const lastPoint = ring[ring.length - 1];

    // Ensure the ring is closed
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        ring.push(firstPoint);
    }

    // Remove consecutive duplicate points
    return ring.filter((point, index, array) => {
        if (index === 0) {
            return true;
        }
        const prevPoint = array[index - 1];
        return point[0] !== prevPoint[0] || point[1] !== prevPoint[1];
    });
};

const cleanGeoJsonObject = (spatialObject: any): any => {
    if (!spatialObject || !spatialObject.type || !Array.isArray(spatialObject.coordinates)) {
        throw new Error("Invalid GeoJSON format");
    }

    // Check if the object is a Polygon or a MultiPolygon
    if (spatialObject.type === "Polygon") {
        // Handle single Polygon
        spatialObject.coordinates[0] = cleanRing(spatialObject.coordinates[0]);
    } else if (spatialObject.type === "MultiPolygon") {
        // Handle MultiPolygon
        spatialObject.coordinates = spatialObject.coordinates.map(polygon => {
            // Clean outer ring for each polygon in the MultiPolygon
            polygon[0] = cleanRing(polygon[0]);
            return polygon;
        });
    } else {
        throw new Error("Unsupported GeoJSON type. Only Polygon and MultiPolygon are supported.");
    }

    return spatialObject;
};

export const fixGeoJsonGeometry = (spatialObject: any): any => {
    // Clean the GeoJSON object
    const cleanedGeoJson = cleanGeoJsonObject(spatialObject);
    const reader = new jsts.io.GeoJSONReader();

    // Read it into JSTS geometry
    let geometry = reader.read(cleanedGeoJson);

    if (!geometry.isValid()) {
        // Fix the geometry using buffer(0) if invalid
        // 0: buffer distance
        // 8: buffer quadrant segments (default)
        // 1: buffer cap style  (default)
        geometry = geometry.buffer(0, 8, 1);
    }

    // Convert the geometry back to GeoJSON format
    const writer = new jsts.io.GeoJSONWriter();
    return writer.write(geometry);
};
